import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import { FrenchService, FrenchLesson, FrenchWord, GrammarPoint, LessonSummary } from '../../services/frenchService';
import PageHeader from '../common/PageHeader';
import GenericHistorySelector from '../shared/GenericHistorySelector';
import GenericPastLessons from '../shared/GenericPastLessons';
import './FrenchLearn.css';

interface FrenchLearnProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type TabType = 'text' | 'vocabulary' | 'grammar';
type ViewMode = 'current' | 'past-list' | 'past-lesson';
type LoadingStage = 'idle' | 'text' | 'vocabulary-grammar';

const FrenchLearn: React.FC<FrenchLearnProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const [lesson, setLesson] = useState<FrenchLesson | null>(null);
  const [selectedWord, setSelectedWord] = useState<FrenchWord | null>(null);
  const [expandedGrammar, setExpandedGrammar] = useState<Set<number>>(new Set());
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastLesson, setSelectedPastLesson] = useState<FrenchLesson | null>(null);
  const [showHistorySelector, setShowHistorySelector] = useState(false);
  const [pastLessonsList, setPastLessonsList] = useState<LessonSummary[]>([]);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImageOnly, setIsGeneratingImageOnly] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load latest lesson from database on mount
  useEffect(() => {
    loadLatestLesson();
  }, []);

  const loadLatestLesson = async () => {
    try {
      const result = await FrenchService.getPastLessons(1);
      if (result.lessons.length > 0) {
        const latestLesson = await FrenchService.getLessonById(result.lessons[0].lesson_id);
        setLesson(latestLesson);
      }
    } catch (err) {
      console.error('Error loading latest lesson:', err);
      // It's okay if there are no lessons yet
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleOpenGenerateLesson = async () => {
    try {
      setShowMenu(false);
      // Fetch past lessons for the selector
      const result = await FrenchService.getPastLessons(100);
      setPastLessonsList(result.lessons);
      setShowHistorySelector(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      // Open selector anyway, it will show empty state
      setPastLessonsList([]);
      setShowHistorySelector(true);
    }
  };

  const handleGenerateLesson = async (selectedIds: string[], customInstruction: string) => {
    try {
      setShowHistorySelector(false);
      setLoadingStage('text');
      setError(null);
      setSelectedWord(null);
      setExpandedGrammar(new Set());
      
      // Generate lesson with progress tracking
      const newLesson = await FrenchService.generateLesson(
        selectedIds,
        customInstruction || undefined,
        (stage) => {
          setLoadingStage(stage);
        }
      );
      
      setLesson(newLesson);
      setLoadingStage('idle');
      setActiveTab('text');
      setViewMode('current');
    } catch (err) {
      console.error('Error generating lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate lesson. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenPastLessons = () => {
    setShowMenu(false);
    setViewMode('past-list');
  };

  const handleSelectPastLesson = (pastLesson: FrenchLesson) => {
    setSelectedPastLesson(pastLesson);
    setViewMode('past-lesson');
    setActiveTab('text');
  };

  const handleBackToCurrentLesson = () => {
    setViewMode('current');
    setSelectedPastLesson(null);
  };

  const handleOpenGenerateImage = () => {
    setShowMenu(false);
    const currentLesson = getCurrentLesson();
    if (currentLesson) {
      setImagePrompt(currentLesson.lesson_objective || '');
      setShowImageGeneratorModal(true);
    }
  };

  const handleGenerateImageOnly = async () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson?.lesson_id || !imagePrompt.trim()) return;

    try {
      setIsGeneratingImageOnly(true);
      setError(null);

      // Generate image
      const imageData = await FrenchService.generateLessonImage(
        currentLesson.lesson_id,
        imagePrompt.trim()
      );

      // Upload to Firebase and save URL
      const imageUrl = await FrenchService.uploadLessonImage(
        currentLesson.lesson_id,
        imageData.image,
        imageData.mimeType,
        imagePrompt.trim()
      );

      // Update lesson with image URL
      const updatedLesson = { ...currentLesson, image_url: imageUrl, image_prompt: imagePrompt.trim() };
      
      // Update the appropriate lesson state
      if (viewMode === 'past-lesson' && selectedPastLesson) {
        setSelectedPastLesson(updatedLesson);
      } else {
        setLesson(updatedLesson);
      }
      
      setShowImageGeneratorModal(false);
      setImagePrompt('');
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImageOnly(false);
    }
  };

  const handleWordClick = (word: FrenchWord) => {
    setSelectedWord(word);
  };

  const toggleGrammarPoint = (index: number) => {
    const newExpanded = new Set(expandedGrammar);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedGrammar(newExpanded);
  };

  // Get the current lesson being displayed
  const getCurrentLesson = () => {
    if (viewMode === 'past-lesson' && selectedPastLesson) {
      return selectedPastLesson;
    }
    return lesson;
  };

  // Render lesson text with clickable words
  const renderLessonText = () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson) return null;

    const { text, words } = currentLesson;
    const segments: JSX.Element[] = [];
    let currentIndex = 0;

    // Create a map for quick word lookup
    const wordMap = new Map<string, FrenchWord>();
    words.forEach(word => wordMap.set(word.word.toLowerCase(), word));

    // Split text into words and spaces
    const textParts = text.split(/(\s+|[.,!?;:])/);
    
    textParts.forEach((part, idx) => {
      // Clean the part for lookup (remove punctuation)
      const cleanPart = part.toLowerCase().replace(/[.,!?;:]/g, '');
      const matchedWord = wordMap.get(cleanPart);

      if (matchedWord && cleanPart.length > 0) {
        // Add clickable word
        segments.push(
          <span
            key={`word-${idx}`}
            className="french-word-clickable"
            onClick={() => handleWordClick(matchedWord)}
          >
            {part}
          </span>
        );
      } else {
        // Add plain text
        segments.push(
          <span key={`text-${idx}`} className="french-text-plain">
            {part}
          </span>
        );
      }
    });

    return <div className="french-text-content">{segments}</div>;
  };

  // Determine current lesson and view state
  const currentLesson = getCurrentLesson();
  const showBackButton = viewMode === 'past-lesson';

  return (
    <div className="french-learn-container">
      {/* If in past lessons list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={FrenchService}
          onSelectLesson={handleSelectPastLesson}
          onClose={handleBackToCurrentLesson}
        />
      )}

      {/* Main lesson view */}
      {viewMode !== 'past-list' && (
        <>
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn french-back-btn"
              onClick={handleBackToCurrentLesson}
              aria-label="Back to current lesson"
            >
              ←
            </button>
          ) : undefined
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-lesson' ? 'Past Lesson' : 'French Learning'}
          </h1>
        }
        rightActions={
          <div className="french-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={loadingStage !== 'idle'}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="french-menu-dropdown">
                <button
                  className="french-menu-item"
                  onClick={handleOpenGenerateLesson}
                >
                  ✨ Generate New Lesson
                </button>
                <button
                  className="french-menu-item"
                  onClick={handleOpenGenerateImage}
                  disabled={!currentLesson}
                >
                  🖼️ Generate Image
                </button>
                <button
                  className="french-menu-item"
                  onClick={handleOpenPastLessons}
                >
                  📚 Past Lessons
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="french-main-content">
        {/* Loading State */}
        {loadingStage !== 'idle' && (
          <div className="french-loading">
            <div className="french-spinner-large"></div>
            <p className="french-loading-text">
              {loadingStage === 'text' ? 'Step 1/2: Generating text...' : 'Step 2/2: Extracting vocabulary and grammar...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && loadingStage === 'idle' && (
          <div className="french-error">
            <p>{error}</p>
          </div>
        )}

        {/* Lesson Content - Text Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'text' && (
          <div className="french-chapter">
            <h1 className="french-lesson-title">{currentLesson.title}</h1>
            {currentLesson.image_url && (
              <img 
                src={currentLesson.image_url} 
                alt={currentLesson.title}
                className="french-lesson-image"
              />
            )}
            {renderLessonText()}
            <div className="french-instructions">
              💡 Click on any word to see its translation and grammar details.
            </div>
          </div>
        )}

        {/* Vocabulary Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'vocabulary' && (
          <div className="french-vocabulary-section">
            <h2 className="french-vocab-section-header">Vocabulary ({currentLesson.words.length} words)</h2>
            {currentLesson.words.length > 0 ? (
              <div className="french-vocab-grid">
                {currentLesson.words.map((word, index) => (
                  <button
                    key={index}
                    className={`french-vocab-item ${selectedWord?.word === word.word ? 'active' : ''}`}
                    onClick={() => handleWordClick(word)}
                  >
                    <span className="french-vocab-word">{word.word}</span>
                    <span className="french-vocab-type">{word.wordType}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="french-no-vocab">No vocabulary available for this lesson.</p>
            )}
          </div>
        )}

        {/* Grammar Tab */}
        {currentLesson && loadingStage === 'idle' && activeTab === 'grammar' && (
          <div className="french-grammar-section">
            <h2 className="french-grammar-section-header">Grammar Points ({currentLesson.grammarPoints?.length || 0})</h2>
            {currentLesson.grammarPoints && currentLesson.grammarPoints.length > 0 ? (
              <div className="french-grammar-list">
                {currentLesson.grammarPoints.map((point, index) => (
                  <div key={index} className="french-grammar-card">
                    <button
                      className="french-grammar-header"
                      onClick={() => toggleGrammarPoint(index)}
                    >
                      <span className="french-grammar-title">{point.title}</span>
                      <span className="french-grammar-arrow">
                        {expandedGrammar.has(index) ? '▼' : '▶'}
                      </span>
                    </button>
                    {expandedGrammar.has(index) && (
                      <div className="french-grammar-content">
                        <p className="french-grammar-explanation">{point.explanation}</p>
                        {point.examples && point.examples.length > 0 && (
                          <div className="french-grammar-examples">
                            <strong>Examples:</strong>
                            <ul>
                              {point.examples.map((example, exIdx) => (
                                <li key={exIdx}>{example}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="french-no-grammar">No grammar points available for this lesson.</p>
            )}
          </div>
        )}

        {/* Welcome Message */}
        {!currentLesson && loadingStage === 'idle' && !error && (
          <div className="french-welcome">
            <h2>Welcome to French Learning</h2>
            <p>Click the menu (⋮) in the top right and select "Generate New Lesson" to create an AI-powered French reading lesson.</p>
            <div className="french-features">
              <div className="french-feature">
                <span className="french-feature-icon">📖</span>
                <span>A1-B2 Level Reading</span>
              </div>
              <div className="french-feature">
                <span className="french-feature-icon">📚</span>
                <span>Vocabulary & Grammar</span>
              </div>
              <div className="french-feature">
                <span className="french-feature-icon">🎯</span>
                <span>Practical Examples</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      {currentLesson && loadingStage === 'idle' && (
        <div className="french-bottom-bar">
          <button
            className={`french-tab-button ${activeTab === 'text' ? 'active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            Text
          </button>
          <button
            className={`french-tab-button ${activeTab === 'vocabulary' ? 'active' : ''}`}
            onClick={() => setActiveTab('vocabulary')}
          >
            Vocabulary ({currentLesson.words?.length || 0})
          </button>
          <button
            className={`french-tab-button ${activeTab === 'grammar' ? 'active' : ''}`}
            onClick={() => setActiveTab('grammar')}
          >
            Grammar ({currentLesson.grammarPoints?.length || 0})
          </button>
        </div>
      )}

      {/* History Selector Modal */}
      <GenericHistorySelector
        isOpen={showHistorySelector}
        pastLessons={pastLessonsList}
        onGenerate={handleGenerateLesson}
        onClose={() => setShowHistorySelector(false)}
        customInstructionPlaceholder="e.g., French cuisine, Travel in Paris, Daily conversations..."
      />

      {/* Word Popup */}
      {selectedWord && (
        <>
          <div className="french-popup-overlay" onClick={() => setSelectedWord(null)} />
          <div className="french-word-popup">
            <button 
              className="french-popup-close" 
              onClick={() => setSelectedWord(null)}
            >
              ×
            </button>
            <div className="french-word-main">{selectedWord.word}</div>
            <div className="french-word-type">{selectedWord.wordType}</div>
            <div className="french-word-english">{selectedWord.english}</div>
            <div className="french-word-forms">
              <div className="french-word-form-item">
                <span className="french-word-form-label">Dictionary form:</span>
                <span className="french-word-form-value">{selectedWord.dictionaryForm}</span>
              </div>
              <div className="french-word-form-item">
                <span className="french-word-form-label">Used as:</span>
                <span className="french-word-form-value">{selectedWord.usedForm}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Image Generator Modal */}
      {showImageGeneratorModal && (
        <>
          <div 
            className="french-image-modal-overlay" 
            onClick={() => !isGeneratingImageOnly && setShowImageGeneratorModal(false)}
          />
          <div className="french-image-modal">
            <div className="french-image-modal-header">
              <h2 className="french-image-modal-title">Generate Image</h2>
              <button 
                className="french-image-modal-close" 
                onClick={() => setShowImageGeneratorModal(false)}
                disabled={isGeneratingImageOnly}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="french-image-modal-content">
              <label htmlFor="image-prompt" className="french-image-modal-label">
                Image Description
              </label>
              <textarea
                id="image-prompt"
                className="french-image-modal-textarea"
                placeholder="Describe the image you want to generate..."
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                disabled={isGeneratingImageOnly}
                rows={4}
              />
              <p className="french-image-modal-hint">
                Provide a detailed description of the image you want to generate for this lesson.
              </p>
            </div>

            <div className="french-image-modal-footer">
              <button 
                className="french-image-modal-btn french-image-modal-btn-cancel"
                onClick={() => setShowImageGeneratorModal(false)}
                disabled={isGeneratingImageOnly}
              >
                Cancel
              </button>
              <button 
                className="french-image-modal-btn french-image-modal-btn-generate"
                onClick={handleGenerateImageOnly}
                disabled={isGeneratingImageOnly || !imagePrompt.trim()}
              >
                {isGeneratingImageOnly ? 'Generating...' : 'Generate Image'}
              </button>
            </div>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default FrenchLearn;

